import React, { useState, useEffect, useRef } from 'react';
import { User, Lock, Eye, EyeOff, Save, X, UploadCloud, Image as ImageIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { Row, Col, Card, Form, Button, Nav, Tab, Alert, Toast, ToastContainer } from 'react-bootstrap';
import { apiService } from '../services/api';
import './Profile.css';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [photoPreview, setPhotoPreview] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  useEffect(() => {
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .profile-container *,
      .profile-container *::before,
      .profile-container *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Load user profile
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await apiService.getUserProfile();
      console.log('Profile loaded:', response); // Debug log
      console.log('Photo data:', response.photo); // Debug log
      setUserProfile(response);
      setFormData({
        firstName: response.firstName || '',
        lastName: response.lastName || '',
        email: response.email || '',
        phone: response.phone || '',
        bio: response.bio || '',
        department: response.department || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPhotoPreview(response.photo || '');
      console.log('Photo preview set to:', response.photo || 'none'); // Debug log
    } catch (err) {
      console.error('Error loading profile:', err); // Debug log
      setError(err.message || 'Failed to load profile');
    }
  };

  const showToastNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const compressImage = (file, maxWidth = 400, maxHeight = 400, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const onSelectFile = async (file) => {
    if (!file) return;
    console.log('File selected:', file.name, file.type, file.size); // Debug log
    
    try {
      // Compress the image before converting to base64
      const compressedBase64 = await compressImage(file);
      console.log('Compressed base64 generated, length:', compressedBase64.length); // Debug log
      console.log('Original size:', file.size, 'bytes, Compressed size:', Math.round(compressedBase64.length * 0.75), 'bytes'); // Debug log
      
      setPhotoPreview(compressedBase64);
      // Optimistically set in state; will be sent on save
      setFormData(prev => ({ ...prev, photo: compressedBase64 }));
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fallback to original method if compression fails
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        setPhotoPreview(base64);
        setFormData(prev => ({ ...prev, photo: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    onSelectFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onSelectFile(e.dataTransfer.files[0]);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prefer updating via users/:id if available; else use auth user profile
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        // Don't include email in payload since it's disabled and shouldn't be updated
        phone: formData.phone,
        bio: formData.bio,
        department: formData.department,
        photo: formData.photo
      };

      console.log('Saving profile with payload:', payload); // Debug log
      console.log('Photo being saved:', formData.photo ? 'Yes, length: ' + formData.photo.length : 'No'); // Debug log

      if (userProfile?.id) {
        await apiService.updateUserProfile(userProfile.id, payload);
      } else {
        await apiService.updateMyProfile(payload);
      }
      
      setSuccess('Profile updated successfully!');
      showToastNotification('Profile updated successfully!', 'success');
      await loadUserProfile(); // Reload profile
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      showToastNotification(err.message || 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await apiService.changePasswordFromProfile(formData.currentPassword, formData.newPassword);
      
      setSuccess('Password updated successfully!');
      showToastNotification('Password updated successfully!', 'success');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update password');
      showToastNotification(err.message || 'Failed to update password', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <Row className="mb-4">
        <Col>
          <h2>Profile Settings</h2>
          <p className="text-muted">Manage your account information and security settings</p>
        </Col>
      </Row>

      <Row>
        <Col lg={4} className="mb-4">
          <Card>
            <Card.Body className="text-center">
              <div
                className={`profile-avatar mb-3 ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                style={{ cursor: 'pointer' }}
                onClick={() => fileInputRef.current?.click()}
              >
                {photoPreview ? (
                  <img 
                    src={photoPreview} 
                    alt="Profile" 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      borderRadius: '50%', 
                      objectFit: 'cover',
                      border: '3px solid rgba(255, 255, 255, 0.9)'
                    }} 
                  />
                ) : (
                  <User size={64} />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <h5 className="mb-1">{formData.firstName} {formData.lastName}</h5>
              <p className="text-muted mb-3">{formData.email}</p>
              <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()}>
                <UploadCloud size={16} className="me-2" />
                Change Photo
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card>
            <Card.Header>
              <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <Nav.Item>
                  <Nav.Link eventKey="personal">Personal Information</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="security">Security</Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body>
              <Tab.Content>
                <Tab.Pane active={activeTab === 'personal'}>
                  <Form onSubmit={handleProfileSubmit}>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Enter first name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Enter last name"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            disabled
                            placeholder="Email cannot be changed"
                            style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                          />
                          <Form.Text className="text-muted">
                            Email cannot be modified for security reasons
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Department</Form.Label>
                          <Form.Control
                            type="text"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="Enter department"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        {/* Empty column for layout balance */}
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Bio</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        placeholder="Tell us a bit about yourself"
                      />
                    </Form.Group>

                    <div className="d-flex gap-2">
                      <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                        <Save size={16} className="me-2" />
                      </Button>
                      <Button variant="outline-secondary" disabled={loading}>
                        <X size={16} className="me-2" />
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </Tab.Pane>

                <Tab.Pane active={activeTab === 'security'}>
                  <Alert variant="info" className="mb-4">
                    <strong>Security Tip:</strong> Use a strong password with at least 8 characters, including uppercase, lowercase, numbers, and special characters.
                  </Alert>

                  <Form onSubmit={handlePasswordSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Password</Form.Label>
                      <div className="input-group">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          name="currentPassword"
                          value={formData.currentPassword}
                          onChange={handleInputChange}
                          placeholder="Enter current password"
                        />
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                      </div>
                    </Form.Group>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>New Password</Form.Label>
                          <div className="input-group">
                            <Form.Control
                              type={showNewPassword ? "text" : "password"}
                              name="newPassword"
                              value={formData.newPassword}
                              onChange={handleInputChange}
                              placeholder="Enter new password"
                            />
                            <Button
                              variant="outline-secondary"
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                              {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </Button>
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>Confirm New Password</Form.Label>
                          <div className="input-group">
                            <Form.Control
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleInputChange}
                              placeholder="Confirm new password"
                            />
                            <Button
                              variant="outline-secondary"
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </Button>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2">
                      <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? 'Updating...' : 'Update Password'}
                        <Lock size={16} className="me-2" />
                      </Button>
                      <Button variant="outline-secondary" disabled={loading}>
                        <X size={16} className="me-2" />
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            {toastType === 'success' ? (
              <CheckCircle className="text-success me-2" size={20} />
            ) : (
              <AlertCircle className="text-danger me-2" size={20} />
            )}
            <strong className="me-auto">
              {toastType === 'success' ? 'Success' : 'Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastType === 'success' ? 'text-success' : 'text-danger'}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Profile; 